/**
 * Правка ВРЕМЕНИ в редакторе живого эфира (пара не «Без автоправок»): только то, что человек
 * изменил сам.
 *
 * Проба 20261103-P50m10d, эфир 8a8caa32, папка 1 en. Перетаскивание, смена длины и сдвиг группы
 * звали раздвижку редактора (cascadeNeighbours → cascadeOverlaps): по копии папки, загруженной до
 * озвучки (длины — слоты, родитель разрезанной фразы ещё жив), она раздвигала соседей и метила их
 * правкой человека, а автосохранение писало каждой реплике `start_rus_milli`, `end_rus_milli` и —
 * на сервере — `length := end − start`, то есть слот копии вместо длины дубля (895432 1731→1060,
 * 895633 1841→1120, 895717 2479→1961). Перетаскивание 893401 на +100 мс в окне до дозагрузки
 * отправило и соседку 895633 как 144249–145369.
 *
 * Теперь на эфире: соседей редактор не трогает (их подгоняет временем круг правки на сервере и
 * называет в отчёте, п.50г); у самой реплики уходит только то, что человек сдвинул: место — если
 * сдвинул, длина — если растянул. Место считается от места реплики в данных (а не от
 * нарисованного: лента без режима рисует наехавшую озвучку отодвинутой — этот отступ не работа
 * человека). Слот (`start_rus_milli`/`end_rus_milli`) правка дубля не меняет вовсе.
 */
import type { AudioTrackData } from "./types";

/** Что человек изменил во времени реплики: место (`start`) и/или длину (`length`). */
export type LiveTimingFields = Partial<Pick<AudioTrackData, "start" | "length">>;

/** Меньше этого — дрожь указателя, а не правка (как `voicingWasMoved` дорожки перевода). */
export const LIVE_TIMING_EPS_MS = 0.5;

/**
 * Поля правки времени по перетаскиванию / растягиванию реплики.
 *
 * `stored` — реплика в данных редактора (json пары), `drawn` — она же на ленте (с чего начали
 * тянуть), `position` / `length` — где отпустили. null — ничего не изменилось.
 */
export function liveTimingFields({
    stored,
    drawn,
    position,
    length,
}: {
    stored?: Pick<AudioTrackData, "start" | "length"> | null;
    drawn?: { start: number; length: number } | null;
    position: number;
    length: number;
}): LiveTimingFields | null {
    const fromStart = drawn ? drawn.start : stored ? stored.start : position;
    const fromLength = drawn ? drawn.length : stored ? stored.length : length;
    const out: LiveTimingFields = {};
    const moved = position - fromStart;
    if (Math.abs(moved) >= LIVE_TIMING_EPS_MS) {
        out.start = (stored ? stored.start : fromStart) + moved;
    }
    if (Math.abs(length - fromLength) >= LIVE_TIMING_EPS_MS) {
        out.length = length;
    }
    return out.start === undefined && out.length === undefined ? null : out;
}

/** Сдвиг группы: место каждой — её место в данных плюс сдвиг. */
export function liveShiftFields(stored: Pick<AudioTrackData, "start">, diff: number): LiveTimingFields | null {
    return Math.abs(diff) >= LIVE_TIMING_EPS_MS ? { start: stored.start + diff } : null;
}

/** Время правки эфира на проводе: `stream_timing` строки сохранения (translate-python stream_editor). */
export function liveTimingWire(fields: Partial<AudioTrackData>): { start?: number; length?: number } | null {
    const out: { start?: number; length?: number } = {};
    if (typeof fields.start === "number" && Number.isFinite(fields.start)) out.start = Math.round(fields.start);
    if (typeof fields.length === "number" && Number.isFinite(fields.length)) out.length = Math.round(fields.length);
    return out.start === undefined && out.length === undefined ? null : out;
}
